import {TColType} from './cell.enum';
import {ICustomColDef} from './interfaces/custom-col-def.interface';

/**
 * Sanitizes input text by removing all characters that are not allowed for numeric input.
 * Keeps only digits, minus sign, comma, and period.
 * @param text - The input text to sanitize
 * @returns The sanitized text containing only allowed characters
 */
const sanitizeNumericInput: (text: string) => string = (text: string): string => {
  if (!text) return '';
  // Allow only digits, minus sign, comma, and period
  return text.replace(/[^\d\-,.]/g, '');
};

/**
 * Enhanced number parser that sanitizes input and handles the German number format.
 * Maps invalid characters to empty strings and converts commas to periods for parsing.
 * @param text - The input text to parse
 * @returns The parsed number or null if invalid
 */
const createNumberParser: () => (text: string) => number = (): (text: string) => number => (text: string): number => {
  if (text === null || text === undefined) return null;

  // Sanitize the input by removing unwanted characters
  const sanitized: string = sanitizeNumericInput(text);

  // If sanitization results in an empty string, return null
  if (!sanitized) return null;

  // Replace commas with periods for proper parsing
  const normalized: string = sanitized.replace(',', '.');

  // Parse the number
  const parsed: number = parseFloat(normalized);

  // Return null if parsing resulted in NaN
  return isNaN(parsed) ? null : parsed;
};

const numberpattern = '\\d.,-\\W\\w\\s€$';

export const ColTypeColDefMap: Record<number, ICustomColDef> = {
  // standard string cell
  [-1]: {
    cellRenderer: 'customCell',
    cellEditor: 'customCellEditor',
    filter: 'agTextColumnFilter',
  },
  [TColType.clValue]: {
    // float values cols
    cellRenderer: 'customCell',
    cellEditor: 'customCellEditor',
    filter: 'agNumberColumnFilter',
    filterParams: {
      allowedCharPattern: numberpattern,
      numberParser: createNumberParser(),
      numberFormatter: (value: number | null): string => {
        return value == null ? null : value.toString().replace(".", ",");
      },
    },
  },
  [TColType.clBool]: {
    // boolean values
    cellRenderer: 'boolCellRenderer',
    cellEditor: 'boolCellEditor',
  },
  [TColType.clColor]: {
    cellRenderer: 'colorCellRenderer',
    cellEditor: 'colorCellEditor',
  },
  // reference to another object --> tree for selection == foreign key reference
  [TColType.clReference]: {
    cellRenderer: 'referenceCell',
    cellEditor: 'referenceCellEditor',
    filter: 'agTextColumnFilter',
  },
  // reference cell with multi-select
  [TColType.clRefSelection]: {
    cellRenderer: 'referenceSelectionCell',
    cellEditor: 'referenceCellEditor',
    filter: 'agTextColumnFilter',
  },
  [TColType.clNumber]: {
    // inter values cols
    cellRenderer: 'customCell',
    cellEditor: 'customCellEditor',
    filter: 'agNumberColumnFilter',
    filterParams: {
      allowedCharPattern: numberpattern,
      numberParser: createNumberParser(),
      numberFormatter: (value: number | null): string => {
        return value == null ? null : value.toString().replace(".", ",");
      },
    },
  },
  // type cell, e.g. state
  [TColType.clEnum]: {
    cellRenderer: 'enumCell',
    cellEditor: 'enumCellEditor',
    filter: 'repositoryFilter',
    cellRendererParams: {
      isMulti: false,
    },
    cellEditorParams: {
      isMulti: false,
    },
  },
  // date cell
  [TColType.clDateTime]: {
    cellRenderer: 'dateCell',
    cellEditor: 'dateCellEditor',
    filter: 'dateFilter',
  },
  // time range in time plan table
  [TColType.clTimeRangeString]: {
    cellRenderer: 'customCell',
    cellEditor: 'timeRangeCellEditor',
    filter: 'agTextColumnFilter',
  },
  // type cell, e.g. state
  [TColType.clEnumMulti]: {
    cellRenderer: 'enumCell',
    cellEditor: 'enumCellEditor',
    filter: 'repositoryFilter',
    cellRendererParams: {
      isMulti: true,
    },
    cellEditorParams: {
      isMulti: true,
    },
  },
  [TColType.clPercentage]: {
    // inter values cols
    cellRenderer: 'percentageCellRenderer',
    cellEditor: 'percentageCellEditor',
    filter: 'agNumberColumnFilter',
    filterParams: {
      allowedCharPattern: numberpattern,
      numberParser: createNumberParser(),
      numberFormatter: (value: number | null): string => {
        return value == null ? null : value.toString().replace(".", ",");
      },
    },
  },
  [TColType.clTimeOfDay]: {
    cellRenderer: 'timeOfDayCellRenderer',
    cellEditor: 'timeOfDayCellEditor',
  },
  [TColType.clAutoComplete]: {
    cellRenderer: 'autocompleteRenderer',
    cellEditor: 'autocompleteEditor',
  },
  [TColType.clDocument]: {
    cellRenderer: 'documentCellRenderer',
    hasEditor: false,
    filter: null,
  },
  // string cell with adjusted renderer for currency
  [TColType.clCurrency]: {
    cellRenderer: 'currencyCell',
    cellEditor: 'customCellEditor',
    filter: 'agNumberColumnFilter',
    filterParams: {
      allowedCharPattern: numberpattern,
      numberParser: createNumberParser(),
      numberFormatter: (value: number | null): string => {
        return value == null ? null : value.toString().replace(".", ",");
      },
    },
  },
  [TColType.clWeekdays]: {
    cellRenderer: 'weekdayCell',
    cellEditor: 'enumCellEditor',
    filter: 'agTextColumnFilter',
    cellRendererParams: {
      isMulti: true,
    },
    cellEditorParams: {
      isMulti: true,
    },
  },
  [TColType.document]: {
    cellRenderer: 'documentCellRenderer',
    hasEditor: false,
    filter: null,
  },
};
